import type { ApiAccessClient, WorkflowType } from '@communities/content-management/domain';
import { useCommunityWorkflows, useUpsertCommunityWorkflow } from '@communities/content-management/hooks';
import { ErrorBanner } from '@core/components';
import { Text, TextInput, IconButton, Add, Delete, IconButtonSize, LoadingSpinner, Dialog } from '@soteria/design-system-ve';
import { useState, useEffect, useRef } from 'react';

import * as styles from './ProviderApplicationDialog.css';

interface ProviderApplicationDialogProps {
  provider?: ApiAccessClient | null;
  workflowType: WorkflowType;
  communityId: string;
  isOpen?: boolean;
  onClose?: () => void;
}

/**
 * Unified dialog for adding or editing authentication providers
 * Handles both create and update scenarios based on whether provider is provided
 */
// Create default provider for new providers
const createDefaultProvider = (): ApiAccessClient => ({
  providerUrl: '',
  clientIdList: [''],
});

// eslint-disable-next-line complexity
export function ProviderApplicationDialog({ provider: initialProvider, workflowType, communityId, isOpen = false, onClose }: ProviderApplicationDialogProps) {
  // Get workflows data and update function
  const { workflows } = useCommunityWorkflows(communityId, workflowType);
  const { upsertWorkflow, loading: isSubmitting, errors, resetErrors } = useUpsertCommunityWorkflow();
  const accessClients = workflows?.apiSettings?.accessClients || [];
  const isEditing = !!initialProvider;

  const [provider, setProvider] = useState<ApiAccessClient>(initialProvider ? { ...initialProvider } : createDefaultProvider());

  // Local state for form values to prevent focus loss during editing
  const [localProviderUrl, setLocalProviderUrl] = useState<string>(initialProvider?.providerUrl || '');
  const [localClientIds, setLocalClientIds] = useState<string[]>(initialProvider?.clientIdList || ['']);

  // Track which input is currently focused
  const [focusedInputIndex, setFocusedInputIndex] = useState<number | null>(null);

  // Ref to store input elements
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Reset form when dialog opens or provider changes
  useEffect(() => {
    if (isOpen) {
      const newProvider = initialProvider ? { ...initialProvider } : createDefaultProvider();

      setProvider(newProvider);
      setLocalProviderUrl(newProvider.providerUrl);
      setLocalClientIds(newProvider.clientIdList);
      resetErrors();
    }
  }, [isOpen, initialProvider, resetErrors]);

  // Only sync local client IDs with provider state when the dialog opens or provider changes
  // This prevents focus loss during typing

  const handleClose = () => {
    const resetProvider = initialProvider ? { ...initialProvider } : createDefaultProvider();

    setProvider(resetProvider);
    setLocalProviderUrl(resetProvider.providerUrl);
    setLocalClientIds(resetProvider.clientIdList);
    resetErrors();
    onClose?.();
  };

  // This function was removed as it's now handled directly in handleSubmit

  // Helper function to create updated workflow configuration
  const createUpdatedWorkflow = (updatedClients: ApiAccessClient[]) => ({
    // Preserve all existing properties
    ...workflows,
    // Ensure type is set
    type: workflows?.type || workflowType,
    // Preserve existing topic actions
    topicActions: workflows?.topicActions || [],
    // Update API settings
    apiSettings: {
      // Preserve existing maxTps and monthlyBudget
      maxTps: workflows?.apiSettings?.maxTps || 100,
      monthlyBudget: workflows?.apiSettings?.monthlyBudget,
      // Only update the access clients
      accessClients: updatedClients,
    },
  });

  // Handle form submission
  const handleSubmit = async () => {
    resetErrors();

    if (!isFormValid) {
      return;
    }

    // Check for duplicates before submission
    const hasDuplicates = localClientIds.some((id, index) => localClientIds.findIndex(otherId => otherId === id) !== index);

    if (hasDuplicates) {
      console.warn('Application IDs must be unique');

      return;
    }

    // Sync local state to provider state before submission
    const updatedProvider = {
      ...provider,
      providerUrl: localProviderUrl,
      clientIdList: localClientIds,
    };

    setProvider(updatedProvider);

    // Get updated clients list using the updated provider
    const updatedClients = initialProvider
      ? accessClients.map(client => (client.providerUrl === initialProvider.providerUrl ? updatedProvider : client))
      : [...accessClients, updatedProvider];

    // Create updated workflow configuration
    const updatedWorkflow = createUpdatedWorkflow(updatedClients);

    // Update workflow
    const success = await upsertWorkflow(communityId, workflowType, updatedWorkflow);

    if (success) {
      // Close dialog after successful submission
      handleClose();
    }
    // If not successful, the error state in the hook will be set
  };

  const handleProviderUrlChange = (value: string) => {
    setLocalProviderUrl(value);
  };

  // Extremely simplified client ID change handler - no duplicate check during typing
  const handleClientIdChange = (idIndex: number, value: string) => {
    // Remember which input is focused
    setFocusedInputIndex(idIndex);

    setLocalClientIds(prev => {
      const updated = [...prev];

      updated[idIndex] = value;

      return updated;
    });
  };

  // Effect to maintain focus after state updates
  useEffect(() => {
    if (focusedInputIndex !== null && inputRefs.current[focusedInputIndex]) {
      // Use setTimeout to ensure this happens after React's rendering
      setTimeout(() => {
        const input = inputRefs.current[focusedInputIndex];

        if (input) {
          input.focus();
        }
      }, 0);
    }
  }, [localClientIds, focusedInputIndex]);

  const handleAddClientId = () => {
    // Use functional update pattern
    setLocalClientIds(prev => [...prev, '']);
  };

  const handleRemoveClientId = (idIndex: number) => {
    // Use functional update pattern
    setLocalClientIds(prev => prev.filter((_, idx) => idx !== idIndex));
  };

  const isFormValid = localProviderUrl.trim() !== '' && localClientIds.every(id => id.trim() !== '');

  return (
    <Dialog
      description={`${isEditing ? 'Edit' : 'Add'} OAuth2 authentication provider and client applications for the ${workflowType} workflow`}
      isOpen={isOpen}
      primaryAction={{
        label: isSubmitting ? <LoadingSpinner /> : isEditing ? 'Update Provider' : 'Add Provider',
        onClick: handleSubmit,
        disabled: isSubmitting || !isFormValid,
      }}
      size="lg"
      title={`${isEditing ? 'Edit' : 'Add'} Provider Application`}
      onClose={handleClose}
    >
      <div className={styles.sectionContainer}>
        {/* Error Banner */}
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error saving the provider application:" />}

        <TextInput
          className={styles.providerInput}
          disabled={isSubmitting}
          label="Provider URL"
          placeholder="https://provider.example.com"
          value={localProviderUrl}
          onChange={handleProviderUrlChange}
        />

        <div className={styles.sectionHeader}>
          <Text>Application (Client) IDs</Text>
        </div>
        {localClientIds.map((clientId, idIndex) => {
          const canRemove = idIndex > 0;

          return (
            <div key={`clientid-${clientId || `empty-${idIndex}`}`} className={styles.clientIdContainer}>
              <TextInput
                ref={el => {
                  // Store reference to the input element
                  inputRefs.current[idIndex] = el;
                }}
                autoFocus={idIndex === localClientIds.length - 1 && idIndex > 0}
                className={styles.clientIdInput}
                disabled={isSubmitting}
                placeholder="Client ID"
                value={clientId}
                onChange={value => handleClientIdChange(idIndex, value)}
                onFocus={() => setFocusedInputIndex(idIndex)}
              />
              <IconButton
                buttonSize={IconButtonSize.Small}
                className={styles.iconButton}
                disabled={isSubmitting}
                icon={canRemove ? Delete : Add}
                onClick={canRemove ? () => handleRemoveClientId(idIndex) : idIndex === 0 ? handleAddClientId : undefined}
              />
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}
